/**
 * Map / Logistics modal — venue name, address, date/time, parking, and
 * refreshments info for the current workshop.
 *
 * Ported from:
 *   - `_legacy/wd.html` lines 137-334 — `#mapModal` shell with the
 *     venue / date-time / transport / access info panel, including the
 *     inline SVG city-block illustration with a pulsing pin at the
 *     workshop venue. The body is a 2-column flex: map fills the left
 *     (`flex: 1`) and a fixed-width 240px info panel sits on the right
 *     with vertically stacked section blocks (venue, date & time,
 *     getting there, access), each separated by a thin true-blue border.
 *   - `_legacy/ws4-session.js` lines 13-26 — venue, date, time,
 *     transit, access field shape.
 *
 * Chrome contract:
 *   - Uses `PanelDialogContent` + the legacy `.modal-shell` markup so
 *     this briefing modal matches the Wave-4 Asset Locker output modals.
 *     Header carries a "VENUE CONFIRMED" green pill on the left cluster
 *     and a pulsing green status dot + "Live" muted label next to the
 *     close button on the right.
 *
 * Theme contract:
 *   - The map SVG and info panel both use CSS variables
 *     (`var(--accent-true-blue)`, `var(--accent-sky)`,
 *     `var(--accent-green)`, `var(--text-*)`, `var(--bg-deep)`,
 *     `var(--bg-panel)`, `var(--bg-panel-alt)`) and rgba() with
 *     `*-rgb` channel vars so the whole modal auto-swaps between
 *     light/dark themes. No hex literals.
 */

import { Calendar, Car, Clock, MapPin, Train, X } from "lucide-react";
import { workshopLabelColor } from "@/components/tags/WorkshopLabelPill";
import { Dialog, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { PanelDialogContent } from "@/components/shell/PanelDialogContent";
import type { CSSProperties, ReactNode } from "react";
import type { LucideIcon } from "lucide-react";
import type { MapContent } from "./modal-content";

type MapModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workshopId: string;
  map: MapContent;
};

/** Uppercase tracked eyebrow label — matches legacy `.meta-label`. */
const META_LABEL_STYLE: CSSProperties = {
  fontSize: "var(--fs-label)",
  letterSpacing: 2,
  textTransform: "uppercase",
  color: "var(--text-muted)",
  fontWeight: 600,
  marginBottom: 8,
};

/** Vertically-stacked info-panel section with a meta label header. */
function InfoBlock({
  label,
  children,
  withBorder = true,
}: {
  label: string;
  children: ReactNode;
  withBorder?: boolean;
}) {
  return (
    <div
      style={{
        padding: 16,
        borderBottom: withBorder
          ? "1px solid rgba(var(--accent-true-blue-rgb), 0.2)"
          : undefined,
      }}
    >
      <div style={META_LABEL_STYLE}>{label}</div>
      {children}
    </div>
  );
}

/** Icon + value row used in date/time and transport blocks. */
function IconRow({
  icon: Icon,
  value,
  marginBottom = 0,
}: {
  icon: LucideIcon;
  value: string;
  marginBottom?: number;
}) {
  return (
    <div
      style={{
        display: "flex",
        gap: 8,
        alignItems: "flex-start",
        marginBottom,
      }}
    >
      <Icon
        aria-hidden
        style={{
          width: 11,
          height: 11,
          color: "var(--accent-sky)",
          flexShrink: 0,
          marginTop: 3,
        }}
        strokeWidth={1.8}
      />
      <span
        style={{
          fontSize: "var(--fs-base)",
          color: "var(--text-secondary)",
          lineHeight: 1.5,
        }}
      >
        {value}
      </span>
    </div>
  );
}

export function MapModal({ open, onOpenChange, workshopId, map }: MapModalProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <PanelDialogContent
        className="w-[82vw] max-w-[960px] border-0 bg-transparent p-0 shadow-none"
        style={{ outline: "none" }}
      >
        <DialogTitle className="sr-only">Logistics — {workshopId}</DialogTitle>
        <DialogDescription className="sr-only">
          Venue details, timing, and access information for {workshopId}.
        </DialogDescription>
        <div
          className="modal-shell"
          style={{ width: "100%", maxHeight: "85vh" }}
        >
          {/* HEADER */}
          <div className="modal-header">
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <MapPin
                aria-hidden
                style={{ width: 14, height: 14, color: "var(--accent-true-blue)" }}
              />
              <span className="modal-title">LOGISTICS</span>
              <span
                style={{
                  padding: "1px 8px",
                  background: "rgba(var(--accent-green-rgb), 0.1)",
                  border: "1px solid var(--accent-green)",
                  borderRadius: 10,
                  fontSize: "var(--fs-label)",
                  color: "var(--accent-green)",
                  fontWeight: 600,
                  letterSpacing: 1,
                }}
              >
                VENUE CONFIRMED
              </span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span
                aria-hidden
                style={{
                  display: "inline-block",
                  width: 7,
                  height: 7,
                  borderRadius: "50%",
                  background: "var(--accent-green)",
                  boxShadow: "var(--glow-green)",
                  animation: "pulse 2s infinite",
                }}
              />
              <span
                style={{
                  fontSize: "var(--fs-label)",
                  color: "var(--text-muted)",
                  letterSpacing: 2,
                  textTransform: "uppercase",
                  marginRight: 12,
                }}
              >
                Live
              </span>
              <button
                type="button"
                onClick={() => onOpenChange(false)}
                className="modal-close-btn"
                aria-label="Close"
              >
                <X aria-hidden style={{ width: 12, height: 12 }} />
                CLOSE
              </button>
            </div>
          </div>

          {/* BODY — 2-column flex: map fills the left, fixed 240px info
              panel on the right. Overrides the default `.modal-body`
              vertical scroll because each column scrolls independently
              (map is non-scrolling, info panel scrolls when content
              overflows). */}
          <div
            className="modal-body activity-body"
            style={{
              display: "flex",
              flex: 1,
              minHeight: 0,
              overflow: "hidden",
              padding: 0,
            }}
          >
            {/* LEFT: Venue map illustration — abstract city blocks with
                a pulsing pin at the workshop venue. Ported verbatim from
                `_legacy/wd.html` lines 156-281 with hex literals swapped
                for CSS vars so the map auto-themes. */}
            <div
              aria-hidden
              style={{
                flex: 1,
                position: "relative",
                overflow: "hidden",
                background:
                  "radial-gradient(ellipse at 40% 50%, var(--bg-deep) 0%, var(--bg-deep) 100%)",
              }}
            >
                <svg
                  viewBox="0 0 600 420"
                  xmlns="http://www.w3.org/2000/svg"
                  preserveAspectRatio="xMidYMid meet"
                  style={{ width: "100%", height: "100%", display: "block" }}
                >
                  <defs>
                    <filter id="mapGlow">
                      <feGaussianBlur stdDeviation="2.5" result="blur" />
                      <feMerge>
                        <feMergeNode in="blur" />
                        <feMergeNode in="SourceGraphic" />
                      </feMerge>
                    </filter>
                    <radialGradient id="pinGrad" cx="50%" cy="30%" r="60%">
                      <stop
                        offset="0%"
                        stopColor="var(--accent-sky)"
                        stopOpacity="0.9"
                      />
                      <stop
                        offset="100%"
                        stopColor="var(--accent-true-blue)"
                        stopOpacity="0.6"
                      />
                    </radialGradient>
                    <radialGradient id="pulseGrad" cx="50%" cy="50%" r="50%">
                      <stop
                        offset="0%"
                        stopColor="var(--accent-sky)"
                        stopOpacity="0.3"
                      />
                      <stop
                        offset="100%"
                        stopColor="var(--accent-sky)"
                        stopOpacity="0"
                      />
                    </radialGradient>
                  </defs>
                  {/* Grid overlay */}
                  <rect width="600" height="420" fill="none" />
                  <g opacity="0.07" stroke="var(--accent-true-blue)" strokeWidth="1">
                    <line x1="0" y1="60" x2="600" y2="60" />
                    <line x1="0" y1="120" x2="600" y2="120" />
                    <line x1="0" y1="180" x2="600" y2="180" />
                    <line x1="0" y1="240" x2="600" y2="240" />
                    <line x1="0" y1="300" x2="600" y2="300" />
                    <line x1="0" y1="360" x2="600" y2="360" />
                    <line x1="75" y1="0" x2="75" y2="420" />
                    <line x1="150" y1="0" x2="150" y2="420" />
                    <line x1="225" y1="0" x2="225" y2="420" />
                    <line x1="300" y1="0" x2="300" y2="420" />
                    <line x1="375" y1="0" x2="375" y2="420" />
                    <line x1="450" y1="0" x2="450" y2="420" />
                    <line x1="525" y1="0" x2="525" y2="420" />
                  </g>
                  {/* City blocks */}
                  <g
                    opacity="0.18"
                    fill="rgba(var(--accent-true-blue-rgb), 0.6)"
                    stroke="var(--accent-true-blue)"
                    strokeWidth="0.5"
                  >
                    <rect x="30" y="30" width="80" height="40" rx="2" />
                    <rect x="30" y="90" width="50" height="55" rx="2" />
                    <rect x="95" y="90" width="60" height="25" rx="2" />
                    <rect x="95" y="130" width="60" height="35" rx="2" />
                    <rect x="30" y="165" width="125" height="30" rx="2" />
                    <rect x="30" y="215" width="60" height="45" rx="2" />
                    <rect x="100" y="215" width="55" height="45" rx="2" />
                    <rect x="30" y="275" width="125" height="30" rx="2" />
                    <rect x="30" y="320" width="80" height="50" rx="2" />
                    <rect x="120" y="320" width="35" height="50" rx="2" />

                    <rect x="175" y="30" width="100" height="40" rx="2" />
                    <rect x="175" y="90" width="45" height="55" rx="2" />
                    <rect x="230" y="90" width="45" height="25" rx="2" />
                    <rect x="175" y="165" width="100" height="30" rx="2" />
                    <rect x="175" y="215" width="100" height="45" rx="2" />
                    <rect x="175" y="275" width="45" height="30" rx="2" />
                    <rect x="230" y="275" width="45" height="30" rx="2" />
                    <rect x="175" y="320" width="100" height="50" rx="2" />

                    <rect x="295" y="30" width="90" height="40" rx="2" />
                    <rect x="295" y="90" width="90" height="55" rx="2" />
                    <rect x="295" y="165" width="40" height="30" rx="2" />
                    <rect x="345" y="165" width="40" height="30" rx="2" />
                    <rect x="295" y="215" width="90" height="45" rx="2" />
                    <rect x="295" y="275" width="90" height="30" rx="2" />
                    <rect x="295" y="320" width="90" height="50" rx="2" />

                    <rect x="405" y="30" width="165" height="40" rx="2" />
                    <rect x="405" y="90" width="75" height="55" rx="2" />
                    <rect x="490" y="90" width="80" height="25" rx="2" />
                    <rect x="490" y="130" width="80" height="35" rx="2" />
                    <rect x="405" y="165" width="165" height="30" rx="2" />
                    <rect x="405" y="215" width="75" height="45" rx="2" />
                    <rect x="490" y="215" width="80" height="45" rx="2" />
                    <rect x="405" y="275" width="165" height="30" rx="2" />
                    <rect x="405" y="320" width="75" height="50" rx="2" />
                    <rect x="490" y="320" width="80" height="50" rx="2" />
                  </g>
                  {/* Roads */}
                  <g
                    stroke="rgba(var(--accent-true-blue-rgb), 0.25)"
                    strokeWidth="1.5"
                    fill="none"
                  >
                    <line x1="0" y1="80" x2="600" y2="80" />
                    <line x1="0" y1="155" x2="600" y2="155" />
                    <line x1="0" y1="205" x2="600" y2="205" />
                    <line x1="0" y1="265" x2="600" y2="265" />
                    <line x1="0" y1="310" x2="600" y2="310" />
                    <line x1="165" y1="0" x2="165" y2="420" />
                    <line x1="285" y1="0" x2="285" y2="420" />
                    <line x1="395" y1="0" x2="395" y2="420" />
                  </g>
                  {/* Main road highlight */}
                  <line
                    x1="0"
                    y1="205"
                    x2="600"
                    y2="205"
                    stroke="rgba(var(--accent-sky-rgb), 0.2)"
                    strokeWidth="3"
                  />
                  <line
                    x1="285"
                    y1="0"
                    x2="285"
                    y2="420"
                    stroke="rgba(var(--accent-sky-rgb), 0.2)"
                    strokeWidth="3"
                  />

                  {/* Pulse rings around pin */}
                  <circle
                    cx="300"
                    cy="210"
                    r="40"
                    fill="url(#pulseGrad)"
                    opacity="0.6"
                  >
                    <animate
                      attributeName="r"
                      values="20;55;20"
                      dur="3s"
                      repeatCount="indefinite"
                    />
                    <animate
                      attributeName="opacity"
                      values="0.6;0;0.6"
                      dur="3s"
                      repeatCount="indefinite"
                    />
                  </circle>
                  <circle
                    cx="300"
                    cy="210"
                    r="28"
                    fill="none"
                    stroke="rgba(var(--accent-sky-rgb), 0.3)"
                    strokeWidth="1"
                  >
                    <animate
                      attributeName="r"
                      values="14;38;14"
                      dur="3s"
                      repeatCount="indefinite"
                      begin="0.5s"
                    />
                    <animate
                      attributeName="opacity"
                      values="0.5;0;0.5"
                      dur="3s"
                      repeatCount="indefinite"
                      begin="0.5s"
                    />
                  </circle>

                  {/* Pin */}
                  <g filter="url(#mapGlow)" transform="translate(285,185)">
                    <path
                      d="M15 0C9.48 0 5 4.48 5 10c0 7.5 10 18 10 18s10-10.5 10-18c0-5.52-4.48-10-10-10z"
                      fill="url(#pinGrad)"
                      stroke="var(--accent-sky)"
                      strokeWidth="1"
                    />
                    <circle
                      cx="15"
                      cy="10"
                      r="4"
                      fill="var(--bg-deep)"
                      opacity="0.8"
                    />
                    <circle cx="15" cy="10" r="2.5" fill="var(--accent-sky)" />
                  </g>

                  {/* Label */}
                  <rect
                    x="220"
                    y="152"
                    width="160"
                    height="22"
                    rx="3"
                    fill="var(--bg-panel)"
                    stroke="rgba(var(--accent-sky-rgb), 0.5)"
                    strokeWidth="1"
                  />
                  <text
                    x="300"
                    y="167"
                    textAnchor="middle"
                    fill="var(--accent-sky)"
                    fontFamily="Inter, sans-serif"
                    fontSize="11"
                    fontWeight="700"
                    letterSpacing="1.5"
                  >
                    WORKSHOP VENUE
                  </text>

                  {/* Compass */}
                  <g transform="translate(555,30)" opacity="0.5">
                    <circle
                      cx="0"
                      cy="0"
                      r="14"
                      fill="none"
                      stroke="rgba(var(--accent-true-blue-rgb), 0.3)"
                      strokeWidth="1"
                    />
                    <text
                      x="0"
                      y="-16"
                      textAnchor="middle"
                      fill="var(--accent-true-blue)"
                      fontFamily="Inter, sans-serif"
                      fontSize="9"
                    >
                      N
                    </text>
                    <line
                      x1="0"
                      y1="-10"
                      x2="0"
                      y2="10"
                      stroke="var(--accent-true-blue)"
                      strokeWidth="1"
                    />
                    <line
                      x1="-10"
                      y1="0"
                      x2="10"
                      y2="0"
                      stroke="rgba(var(--accent-true-blue-rgb), 0.4)"
                      strokeWidth="1"
                    />
                    <polygon
                      points="0,-9 -3,0 0,-3 3,0"
                      fill="var(--accent-sky)"
                      opacity="0.9"
                    />
                  </g>

                  {/* Scale bar */}
                  <g transform="translate(30,395)" opacity="0.5">
                    <line
                      x1="0"
                      y1="0"
                      x2="60"
                      y2="0"
                      stroke="var(--accent-true-blue)"
                      strokeWidth="1"
                    />
                    <line
                      x1="0"
                      y1="-4"
                      x2="0"
                      y2="4"
                      stroke="var(--accent-true-blue)"
                      strokeWidth="1"
                    />
                    <line
                      x1="60"
                      y1="-4"
                      x2="60"
                      y2="4"
                      stroke="var(--accent-true-blue)"
                      strokeWidth="1"
                    />
                    <text
                      x="30"
                      y="-6"
                      textAnchor="middle"
                      fill="var(--text-muted)"
                      fontFamily="Inter, sans-serif"
                      fontSize="9"
                    >
                      500m
                    </text>
                  </g>
              </svg>
            </div>
            {/* RIGHT: 240px info panel — venue, date & time, getting
                there, access. Scrolls independently of the map column. */}
            <div
              className="activity-rail-end"
              style={{
                background: "var(--bg-panel-alt)",
                overflowY: "auto",
                display: "flex",
                flexDirection: "column",
                scrollbarWidth: "thin",
                scrollbarColor:
                  "rgba(var(--accent-true-blue-rgb), 0.4) transparent",
              }}
            >
              {/* VENUE */}
              <InfoBlock label="VENUE">
                <div
                  style={{
                    fontFamily: "Inter, sans-serif",
                    fontSize: "var(--fs-title)",
                    fontWeight: 700,
                    letterSpacing: 1,
                    color: "var(--text-primary)",
                    marginBottom: 4,
                  }}
                >
                  {map.venueName}
                </div>
                <div
                  style={{
                    fontSize: "var(--fs-base)",
                    color: "var(--text-secondary)",
                    lineHeight: 1.5,
                  }}
                >
                  {map.address}
                </div>
              </InfoBlock>

              {/* DATE & TIME */}
              <InfoBlock label="DATE & TIME">
                <IconRow icon={Calendar} value={map.date} marginBottom={8} />
                <IconRow icon={Clock} value={map.time} />
              </InfoBlock>

              {/* GETTING THERE — legacy ws4-session has no transit field
                  in our MapContent shape, so we surface parking here.
                  The train icon is a placeholder for the public-transport
                  line when transit text is added to MapContent later. */}
              <InfoBlock label="GETTING THERE">
                <IconRow
                  icon={Train}
                  value="Nearest stations: Canary Wharf (Jubilee/Elizabeth) — 5 min walk."
                  marginBottom={8}
                />
                <IconRow icon={Car} value={map.parking} />
              </InfoBlock>

              {/* ACCESS — green status chip + refreshments / additional
                  on-site notes. */}
              <InfoBlock label="ACCESS" withBorder={false}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 7,
                    padding: "7px 10px",
                    background: "rgba(var(--accent-green-rgb), 0.06)",
                    border: "1px solid rgba(var(--accent-green-rgb), 0.2)",
                    borderRadius: 8,
                    marginBottom: 8,
                  }}
                >
                  <span
                    aria-hidden
                    style={{
                      display: "inline-block",
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      background: "var(--accent-green)",
                    }}
                  />
                  <span
                    style={{
                      fontSize: "var(--fs-base)",
                      letterSpacing: 1,
                      fontWeight: 600,
                      color: "var(--accent-green)",
                    }}
                  >
                    BADGE REQUIRED
                  </span>
                </div>
                <div
                  style={{
                    fontSize: "var(--fs-base)",
                    color: "var(--text-secondary)",
                    lineHeight: 1.5,
                  }}
                >
                  {map.refreshments}
                </div>
              </InfoBlock>
            </div>
          </div>

          {/* FOOTER — matches legacy `_legacy/wd.html` line 327-332:
              XAES Phase Zero | WS4 – Human-Agent Ways of Working, with
              the workshop id pushed to the right via `.modal-footer-right`. */}
          <div className="modal-footer">
            <span className="modal-footer-label">XAES Phase Zero</span>
            <span className="modal-footer-sep">|</span>
            <span
              className="modal-footer-ws"
              style={{ color: workshopLabelColor(workshopId) }}
            >
              {workshopId} – Human-Agent Ways of Working
            </span>
            <span
              className="modal-footer-right"
              style={{ color: workshopLabelColor(workshopId) }}
            >
              {map.time}
            </span>
          </div>
        </div>
      </PanelDialogContent>
    </Dialog>
  );
}
